import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';

export type PycadPanelId = string;

export type CoordinateSystem = 'LPS' | 'RAS';

export type PanoramicInspectorTab = 'panoramic' | 'utility';

/**
 * Tracks pycad-shell UI state that isn't tied to a specific feature store:
 *  - which right-side panel is currently open
 *  - the resizable panel width (persisted)
 *  - the displayed coordinate system (display-only; VolView is LPS internally)
 */
export const usePycadUiStore = defineStore('pycad-ui', () => {
  const activePanel = ref<PycadPanelId | null>('data');
  const panelWidth = useLocalStorage<number>('pycad-ui:panelWidth', 320);
  const coordinateSystem = useLocalStorage<CoordinateSystem>(
    'pycad-ui:coordinateSystem',
    'LPS'
  );

  /**
   * Whether the 3D volume rendering is shown. Defaults to off so a freshly
   * loaded scan only shows the 2D MPR views (matching pycad's behavior).
   * Not persisted: every new session / image starts with VR disabled.
   */
  const volumeRenderingEnabled = ref(false);

  /**
   * Whether the three MPR cutting planes are shown in the 3D view while the
   * Crosshairs tool is active. Defaults to on so the planes appear as soon as
   * the user activates crosshairs; the user can hide them with the in-view
   * toggle button without affecting the crosshair widget itself.
   */
  const mprPlanesEnabled = ref(true);

  /**
   * Whether the Curved MPR / Panoramic workspace replaces the standard
   * viewport grid. This is a top-toolbar toggle: when on, App.vue swaps the
   * usual LayoutGrid for the dedicated dental workspace (axial spline editor
   * + panoramic + perpendicular cross-section). Not persisted: starts off on
   * every page load so the user enters the normal MPR layout first.
   */
  const curvedMPRWorkspaceActive = ref(false);

  /**
   * Booting flag for the curved MPR workspace.
   *
   * Set to `true` the *instant* the user clicks the toolbar toggle (before
   * the workspace component is even mounted) so a lightweight loading
   * overlay can paint immediately. The workspace clears the flag once its
   * vtk views are attached and ready for interaction.
   *
   * Without this, the heavy synchronous setup inside the workspace (worker
   * download, vtk renderer / interactor construction × 3, widget manager
   * initialization, etc.) would block the first browser paint, making the
   * UI feel frozen for the first second or two after the click.
   */
  const curvedMPRBooting = ref(false);

  /**
   * Panoramic workspace inspector: a permanent primary tab plus at most one
   * replaceable utility tab from the standard toolbar panels. Isolated from
   * `activePanel`, which remains the standard viewer's panel state.
   */
  const panoramicSecondaryPanel = ref<PycadPanelId | null>(null);
  const panoramicInspectorTab = ref<PanoramicInspectorTab>('panoramic');
  const isPanoramicInspectorOpen = ref(false);

  // Clamp the persisted width on load (in case the user dragged it tiny last session).
  if (panelWidth.value < 220) panelWidth.value = 220;
  if (panelWidth.value > 600) panelWidth.value = 600;

  watch(panelWidth, (w) => {
    if (w < 220) panelWidth.value = 220;
    if (w > 600) panelWidth.value = 600;
  });

  function openPanel(id: PycadPanelId) {
    activePanel.value = id;
  }

  function togglePanel(id: PycadPanelId) {
    activePanel.value = activePanel.value === id ? null : id;
  }

  function closePanel() {
    activePanel.value = null;
  }

  function setPanelWidth(w: number) {
    panelWidth.value = Math.max(220, Math.min(600, w));
  }

  function setCoordinateSystem(sys: CoordinateSystem) {
    coordinateSystem.value = sys;
  }

  function setVolumeRenderingEnabled(enabled: boolean) {
    volumeRenderingEnabled.value = enabled;
  }

  function toggleVolumeRendering() {
    volumeRenderingEnabled.value = !volumeRenderingEnabled.value;
  }

  function setMprPlanesEnabled(enabled: boolean) {
    mprPlanesEnabled.value = enabled;
  }

  function toggleMprPlanes() {
    mprPlanesEnabled.value = !mprPlanesEnabled.value;
  }

  function enterPanoramicInspector() {
    isPanoramicInspectorOpen.value = true;
    panoramicInspectorTab.value = 'panoramic';
    panoramicSecondaryPanel.value = null;
  }

  function resetPanoramicInspector() {
    isPanoramicInspectorOpen.value = false;
    panoramicSecondaryPanel.value = null;
    panoramicInspectorTab.value = 'panoramic';
  }

  function openPanoramicUtilityPanel(id: PycadPanelId) {
    if (!isPanoramicInspectorOpen.value) {
      enterPanoramicInspector();
    }
    panoramicSecondaryPanel.value = id;
    panoramicInspectorTab.value = 'utility';
  }

  function focusPanoramicInspectorTab(tab: PanoramicInspectorTab) {
    panoramicInspectorTab.value = tab;
  }

  function closePanoramicUtilityPanel() {
    panoramicSecondaryPanel.value = null;
    panoramicInspectorTab.value = 'panoramic';
  }

  function togglePanoramicUtilityPanel(id: PycadPanelId) {
    if (panoramicSecondaryPanel.value === id) {
      if (panoramicInspectorTab.value === 'utility') {
        closePanoramicUtilityPanel();
      } else {
        panoramicInspectorTab.value = 'utility';
      }
    } else {
      openPanoramicUtilityPanel(id);
    }
  }

  function setCurvedMPRWorkspaceActive(active: boolean) {
    if (active && !curvedMPRWorkspaceActive.value) {
      // Same deferred-mount trick as the toolbar toggle so this entry path
      // (state-file deserialize, etc.) also gets a snappy overlay.
      curvedMPRBooting.value = true;
      requestAnimationFrame(() => {
        curvedMPRWorkspaceActive.value = true;
        enterPanoramicInspector();
      });
    } else if (!active) {
      curvedMPRWorkspaceActive.value = false;
      curvedMPRBooting.value = false;
      resetPanoramicInspector();
    }
  }

  function toggleCurvedMPRWorkspace() {
    if (curvedMPRWorkspaceActive.value) {
      curvedMPRWorkspaceActive.value = false;
      curvedMPRBooting.value = false;
      resetPanoramicInspector();
      return;
    }
    // Show the overlay first, mount the heavy workspace next paint.
    curvedMPRBooting.value = true;
    requestAnimationFrame(() => {
      curvedMPRWorkspaceActive.value = true;
      enterPanoramicInspector();
    });
  }

  function clearCurvedMPRBooting() {
    curvedMPRBooting.value = false;
  }

  const isPanelOpen = computed(() => {
    if (curvedMPRWorkspaceActive.value) {
      return isPanoramicInspectorOpen.value;
    }
    return activePanel.value !== null;
  });

  return {
    activePanel,
    panelWidth,
    coordinateSystem,
    volumeRenderingEnabled,
    mprPlanesEnabled,
    curvedMPRWorkspaceActive,
    curvedMPRBooting,
    panoramicSecondaryPanel,
    panoramicInspectorTab,
    isPanoramicInspectorOpen,
    isPanelOpen,
    openPanel,
    togglePanel,
    closePanel,
    setPanelWidth,
    setCoordinateSystem,
    setVolumeRenderingEnabled,
    toggleVolumeRendering,
    setMprPlanesEnabled,
    toggleMprPlanes,
    setCurvedMPRWorkspaceActive,
    toggleCurvedMPRWorkspace,
    clearCurvedMPRBooting,
    enterPanoramicInspector,
    resetPanoramicInspector,
    openPanoramicUtilityPanel,
    focusPanoramicInspectorTab,
    closePanoramicUtilityPanel,
    togglePanoramicUtilityPanel,
  };
});
